import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button, Space, Spin, Typography } from 'antd';
import { Link } from 'react-router-dom';

import { JsonBlock } from '../Assessments/shared';
import { conflictingJob, JobWatchStall, runJob } from './actionsApi';
import { readableError } from './errors';
import {
  describeActor,
  formatDuration,
  isJobLive,
  jobKindLabel,
  jobOutcome,
  JobStatusTag,
  useNow,
} from './Jobs';
import { jobLinkHref } from './JobDetail';
import { JobKind, JsonObject, ScenarioJob } from './types';

/**
 * Running one job from an action panel, and rendering its outcome.
 *
 * The words a job is described with — the statuses, the refusal-versus-failure
 * distinction, the LOST sentence — belong to the Jobs screen and are imported
 * from it, so a mint that refuses reads the same on the draft page, on the
 * jobs tab and in a job's own detail. What this module adds is the panel
 * shape around them and the "somebody else is already doing this" case, which
 * only a submitting screen can hit.
 */

const JobLinks: React.FC<{ job: ScenarioJob }> = ({ job }) => {
  const links = (job.links || [])
    .map(link => ({ link, href: jobLinkHref(link) }))
    .filter(entry => entry.href);
  if (!links.length) return null;
  return (
    <Space size={[8, 8]} wrap style={{ marginTop: 8 }}>
      {links.map(({ link, href }) => (
        <Link key={`${link.rel}-${link.id}`} to={String(href)}>
          <Button size="small">{link.label || link.rel}</Button>
        </Link>
      ))}
    </Space>
  );
};

/**
 * What a panel says about a job it stopped watching before it finished.
 *
 * Both cases are ordinary and neither is an error: a queue with no worker
 * draining it, and a job that is genuinely taking longer than a drawer should
 * sit open for. What matters is that the panel stops claiming to be waiting —
 * an indefinite "PENDING · Waiting for a worker" with no clock is
 * indistinguishable from a hang, and it is what an admin watched for sixty
 * seconds while the job it was about had already refused.
 */
export const stallCopy = (
  stall: JobWatchStall,
): { title: string; description: string } => {
  const waited = formatDuration(stall.waitedMs / 1000);
  if (stall.kind === 'unclaimed') {
    return {
      title: `No worker picked this up in ${waited}`,
      description:
        'The job is queued and nothing has claimed it, which usually means ' +
        'the background workers are not running. Nothing has been written. ' +
        'The job keeps its place in the queue — follow it on the Jobs tab, ' +
        'or cancel it there.',
    };
  }
  return {
    title: `This panel stopped waiting after ${waited}`,
    description:
      'The job is still running and nothing here was cancelled. Follow it to ' +
      'the end on the Jobs tab, where its outcome and everything it wrote ' +
      'are recorded.',
  };
};

/** The stall notice, and the link out of it. */
export const JobStallNotice: React.FC<{ stall: JobWatchStall | null }> = ({
  stall,
}) => {
  if (!stall) return null;
  const copy = stallCopy(stall);
  return (
    <Alert
      className="assessment-callout"
      type="warning"
      showIcon
      data-testid="job-stalled"
      title={copy.title}
      description={
        <Space direction="vertical" size={4}>
          <span>{copy.description}</span>
          <Link to="/dashboard/scenario-builder?tab=jobs">
            <Button size="small">Open the Jobs tab</Button>
          </Link>
        </Space>
      }
    />
  );
};

/**
 * Whatever a job row currently says. `successTitle` lets each panel name its
 * own success in its own words; every other status is worded centrally.
 *
 * A LIVE job also carries a clock. Elapsed time is the difference between "it
 * is working" and "it is stuck", and it is the only thing on screen that
 * moves while a worker is thinking.
 */
export const JobOutcomePanel: React.FC<{
  job: ScenarioJob | null;
  successTitle?: string;
  /** Milliseconds since this panel submitted, while it is still watching. */
  elapsedMs?: number | null;
  children?: React.ReactNode;
}> = ({ job, successTitle, elapsedMs, children }) => {
  if (!job) return null;
  const outcome = jobOutcome(job);
  const live = isJobLive(job.status);
  const refusal = job.refusal || {};

  return (
    <Alert
      className="assessment-callout"
      type={outcome.tone}
      showIcon
      icon={live ? <Spin size="small" /> : undefined}
      title={
        <Space>
          <JobStatusTag status={job.status} />
          <span>
            {outcome.tone === 'success' && successTitle
              ? successTitle
              : outcome.title}
          </span>
          {live && elapsedMs != null && (
            <Typography.Text type="secondary" data-testid="job-elapsed">
              {formatDuration(elapsedMs / 1000)} elapsed
            </Typography.Text>
          )}
        </Space>
      }
      description={
        <div>
          {outcome.detail && (
            <Typography.Paragraph>{outcome.detail}</Typography.Paragraph>
          )}
          {outcome.remedy && (
            <Typography.Paragraph>
              <Typography.Text strong>What fixes it: </Typography.Text>
              {outcome.remedy}
            </Typography.Paragraph>
          )}
          {job.status === 'REFUSED' &&
            refusal.details &&
            Object.keys(refusal.details).length > 0 && (
              <JsonBlock compact value={refusal.details} />
            )}
          {job.status === 'FAILED' && job.log && job.log.length > 0 && (
            <pre className="assessment-json compact">{job.log.join('\n')}</pre>
          )}
          <Typography.Text type="secondary">
            {jobKindLabel(job.kind)} · job {job.id}
          </Typography.Text>
          <JobLinks job={job} />
          {children}
        </div>
      }
    />
  );
};

/**
 * A single job at a time, with its transport failure and the live job a 409
 * named kept apart from the job's own outcome: "somebody else is already
 * minting this draft" is a different sentence from "the mint refused".
 */
export const useJobRun = ({
  unclaimedAfterMs,
}: { unclaimedAfterMs?: number } = {}) => {
  const [job, setJob] = useState<ScenarioJob | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState<ScenarioJob | null>(null);
  const [stall, setStall] = useState<JobWatchStall | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const cancelledRef = useRef(false);
  // The clock only ticks while something is being waited on, so an idle
  // drawer costs nothing.
  const now = useNow(1000, running);

  const run = useCallback(
    async (
      kind: JobKind,
      payload: JsonObject = {},
    ): Promise<ScenarioJob | null> => {
      cancelledRef.current = false;
      setRunning(true);
      setError('');
      setConflict(null);
      setStall(null);
      setJob(null);
      setStartedAt(Date.now());
      try {
        const finished = await runJob(kind, payload, {
          onUpdate: setJob,
          onStalled: setStall,
          unclaimedAfterMs,
          cancelled: () => cancelledRef.current,
        });
        setJob(finished);
        return finished;
      } catch (requestError) {
        const live = conflictingJob(requestError);
        if (live) setConflict(live);
        else setError(readableError(requestError, { what: 'That job' }));
        return null;
      } finally {
        setRunning(false);
      }
    },
    [unclaimedAfterMs],
  );

  const reset = useCallback(() => {
    cancelledRef.current = true;
    setJob(null);
    setError('');
    setConflict(null);
    setStall(null);
    setStartedAt(null);
    setRunning(false);
  }, []);

  return {
    job,
    running,
    error,
    conflict,
    /** Set when the watch ended without the job finishing. */
    stall,
    /** Milliseconds since submit, while this panel is still watching. */
    elapsedMs: startedAt == null || !running ? null : now - startedAt,
    run,
    reset,
  };
};

/** The "somebody is already doing this" panel, in the words an admin needs:
 *  who, what, and since when. */
export const ConflictNotice: React.FC<{ job: ScenarioJob | null }> = ({
  job,
}) =>
  job ? (
    <Alert
      className="assessment-callout"
      type="warning"
      showIcon
      title="Another run of this action is already live"
      description={
        <Typography.Text>
          {jobKindLabel(job.kind)} on{' '}
          {job.target_label || `${job.target_kind} #${job.target_id}`} is{' '}
          {String(job.status).toLowerCase()}, started by{' '}
          {describeActor(job.created_by)}
          {job.created_at
            ? ` at ${new Date(job.created_at).toLocaleString()}`
            : ''}
          . Wait for it to finish, or cancel it on the Jobs tab.
        </Typography.Text>
      }
    />
  ) : null;
